import React /*useState*/ from "react";
import PropTypes from "prop-types";
import InvoiceTable from "./InvoiceTable";
import {
  USDollar,
  formatDateForDetailsView,
} from "../../utilities/functions/Helpers";
import FacilityConstants from "../../utilities/constants/FacilityConstants";

const userShape = PropTypes.shape({
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  email: PropTypes.string,
  phone: PropTypes.string,
  street: PropTypes.string,
  city: PropTypes.string,
  state: PropTypes.string,
  zipCode: PropTypes.string,
});

const addOnLineItemShape = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string,
  quantity: PropTypes.number,
  unitPrice: PropTypes.number,
  amount: PropTypes.number,
});

const pricingBreakdownShape = PropTypes.shape({
  subtotal: PropTypes.number,
  tax: PropTypes.number,
  total: PropTypes.number,
  taxRate: PropTypes.number,
  facilityAmount: PropTypes.number,
  facilityRate: PropTypes.number,
  seatAmount: PropTypes.number,
  seatRate: PropTypes.number,
  seatCount: PropTypes.number,
  cleaningAmount: PropTypes.number,
  cleaningRate: PropTypes.number,
  overtimeAmount: PropTypes.number,
  overtimeRate: PropTypes.number,
  overtimeHours: PropTypes.number,
  bookedHours: PropTypes.number,
  hourlyRate: PropTypes.number,
  pricingStrategy: PropTypes.string,
  addOns: PropTypes.arrayOf(addOnLineItemShape),
});

const invoiceShape = PropTypes.shape({
  invoiceNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  issuedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  dueDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  user: userShape,
  pricingBreakdown: pricingBreakdownShape,
});

/**
 * The component to display the invoice.
 * @param {object} props  The props passed to the component.
 * @returns  The component to display the invoice.
 */
const Invoice = ({ invoice }) => {
  const user = invoice?.user;
  const pricingBreakdown = invoice?.pricingBreakdown;

  if (!pricingBreakdown || !user) {
    return (
      <div className="flex items-center justify-center min-h-[320px] rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400">
        Loading invoice details...
      </div>
    );
  }

  const totalPrice = Number(pricingBreakdown.total ?? 0);
  const subTotal = Number(pricingBreakdown.subtotal ?? 0);
  const tax = Number(pricingBreakdown.tax ?? 0);

  return (
    <div
      className="flex flex-col max-w-5xl mx-auto bg-white dark:bg-slate-900 shadow-xl rounded-2xl overflow-hidden print:shadow-none print:rounded-none print:max-w-none"
      id="invoice"
    >
      {/* ----- Header Section ----- */}
      <div className="bg-slate-50 dark:bg-slate-950/50 px-8 py-10 md:px-12 md:py-12 border-b border-slate-100 dark:border-slate-800 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <img
            className="h-16 w-auto object-contain"
            src="/assets/images/le_prestige_hall_logo.png"
            alt="Le Prestige Hall"
          />
        </div>
        <div className="text-right">
          <h1 className="text-4xl font-bold text-slate-900 dark:text-white tracking-tight font-serif mb-1">
            INVOICE
          </h1>
          <p className="text-slate-500 dark:text-slate-400 font-medium text-lg">
            #{invoice.invoiceNumber}
          </p>
        </div>
      </div>

      {/* ----- Info Grid ----- */}
      <div className="px-8 py-10 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-12">
        {/* From */}
        <div className="space-y-4">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            From
          </h3>
          <div className="space-y-1">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white">
              Le Prestige Hall, Inc.
            </h2>
            <div className="text-slate-600 dark:text-slate-300 space-y-1 text-sm leading-relaxed">
              <p>{FacilityConstants.ADDRESS.Street}</p>
              <p>
                {FacilityConstants.ADDRESS.City},{" "}
                {FacilityConstants.ADDRESS.State}{" "}
                {FacilityConstants.ADDRESS.ZipCode}
              </p>
              <p className="pt-2">
                <span className="font-medium text-slate-900 dark:text-slate-200">
                  Email:
                </span>{" "}
                {FacilityConstants.EMAILS.invoices}
              </p>
              <p>
                <span className="font-medium text-slate-900 dark:text-slate-200">
                  Phone:
                </span>{" "}
                {FacilityConstants.PHONE.format1}
              </p>
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className="space-y-4">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            Bill To
          </h3>
          <div className="space-y-1">
            <div className="text-xl font-bold text-slate-900 dark:text-white min-h-[1.75rem]">
              {user.firstName} {user.lastName}
            </div>
            <div className="text-slate-600 dark:text-slate-300 space-y-1 text-sm leading-relaxed">
              <p>{user.street}</p>
              <p>
                {user.city}, {user.state} {user.zipCode}
              </p>
              <p className="pt-2">{user.email}</p>
              <p>{user.phone}</p>
            </div>
          </div>
        </div>
      </div>

      {/* ----- Key Dates Ribbon ----- */}
      <div className="mx-8 md:mx-12 mb-10 bg-slate-50 dark:bg-slate-800/50 rounded-xl px-8 py-6 grid grid-cols-1 sm:grid-cols-3 gap-6 border border-slate-100 dark:border-slate-800">
        <div>
          <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
            Issue Date
          </p>
          <p className="font-semibold text-slate-900 dark:text-slate-200">
            {formatDateForDetailsView(invoice.issuedDate)}
          </p>
        </div>
        <div>
          <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
            Due Date
          </p>
          <p className="font-semibold text-slate-900 dark:text-slate-200">
            {formatDateForDetailsView(invoice.dueDate)}
          </p>
        </div>
        <div>
          <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
            Payment Terms
          </p>
          <p className="font-semibold text-slate-900 dark:text-slate-200">
            Values Due on Receipt
          </p>
        </div>
      </div>

      {/* ----- Invoice Items Table ----- */}
      <div className="px-8 md:px-12 mb-8">
        <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">
          Itemized charges are provided directly from the invoice pricing breakdown stored for this reservation.
        </p>
        <InvoiceTable pricingBreakdown={pricingBreakdown} />
      </div>

      {/* ----- Invoice Summary ----- */}
      <div className="px-8 md:px-12 pb-12 flex flex-col md:flex-row justify-end">
        <div className="w-full md:w-1/2 lg:w-1/3 bg-slate-50 dark:bg-slate-800/50 rounded-xl p-6 space-y-3">
          <div className="flex justify-between items-center text-sm">
            <span className="font-medium text-slate-600 dark:text-slate-400">
              Subtotal
            </span>
            <span className="font-semibold text-slate-900 dark:text-slate-200">
              {USDollar.format(subTotal.toFixed(2))}
            </span>
          </div>

          <div className="flex justify-between items-center text-sm pb-3 border-b border-slate-200 dark:border-slate-700">
            <span className="font-medium text-slate-600 dark:text-slate-400">
              Tax
            </span>
            <span className="font-semibold text-slate-900 dark:text-slate-200">
              {USDollar.format(tax.toFixed(2))}
            </span>
          </div>

          <div className="flex justify-between items-center text-lg pt-1">
            <span className="font-bold text-slate-900 dark:text-white">
              Total
            </span>
            <span className="font-bold text-blue-600 dark:text-blue-400 text-xl">
              {USDollar.format(totalPrice.toFixed(2))}
            </span>
          </div>
        </div>
      </div>

      {/* ----- Footer ----- */}
      <div className="bg-slate-50 dark:bg-slate-950/50 px-8 py-6 border-t border-slate-100 dark:border-slate-800 text-center">
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-2">
          Thank you for choosing Le Prestige Hall. If you have any questions
          about this invoice, please contact us.
        </p>
        <p className="text-slate-400 dark:text-slate-500 text-xs italic print:hidden">
          * For best results, please print this page in Landscape orientation.
        </p>
      </div>
    </div>
  );
};

Invoice.propTypes = {
  invoice: invoiceShape,
};

export default Invoice;
